import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthContext } from "../Hooks/useAuthContext";
import {
  albumSummaries,
  isTestEpisode,
  isVideoTrack,
  loadPodcastData,
  playbackUrl,
} from "./catalog";

const PodcastContext = createContext(null);
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

function storageKey(accountId, name) {
  return `wwt-podcast:${accountId}:${name}`;
}

function readStore(accountId, name, fallback) {
  try {
    const raw = localStorage.getItem(storageKey(accountId, name));
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeStore(accountId, name, value) {
  localStorage.setItem(storageKey(accountId, name), JSON.stringify(value));
  return value;
}

function pickNext(length, current) {
  if (length < 2) return current;
  return (current + 1 + Math.floor(Math.random() * (length - 1))) % length;
}

export function PodcastProvider({ children }) {
  const { user } = useAuthContext();
  const accountId = String(user?.user?._id || "guest");
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const stateRef = useRef({ queue: [], index: 0, repeat: false, shuffle: false, speed: 1 });
  const heardRef = useRef(new Set());
  const [tracks, setTracks] = useState([]);
  const [testTracks, setTestTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [ratings, setRatings] = useState({});
  const [recentIds, setRecentIds] = useState([]);
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [shuffle, setShuffle] = useState(false);
  const [repeatOne, setRepeatOne] = useState(false);
  const [videoMode, setVideoMode] = useState(false);

  useEffect(() => {
    setFavorites(readStore(accountId, "favorites", []));
    setRatings(readStore(accountId, "ratings", {}));
    setRecentIds(readStore(accountId, "recent", []));
  }, [accountId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await loadPodcastData();
      setTracks(data.tracks);
      setTestTracks(data.testTracks || []);
      setAlbums(data.albums);
      setReady(true);
    } catch (loadError) {
      setError(loadError.message || "Could not load podcast");
    } finally {
      setLoading(false);
    }
  }, []);

  // Recent history is local, while episodes can be deleted from another client.
  // Reconcile the stored IDs after a successful library load.
  useEffect(() => {
    if (!ready) return;
    const tracksById = new Map(tracks.map((track) => [String(track._id), track]));
    const next = recentIds.filter((id) => {
      const track = tracksById.get(String(id));
      return track && !isTestEpisode(track);
    });
    if (next.length !== recentIds.length) {
      writeStore(accountId, "recent", next);
      setRecentIds(next);
    }
  }, [accountId, ready, recentIds, tracks]);

  const remember = useCallback((track) => {
    setRecentIds((prev) => {
      const next = [track._id, ...prev.filter((id) => id !== track._id)].slice(0, 10);
      writeStore(accountId, "recent", next);
      return next;
    });
  }, [accountId]);

  const noteListen = useCallback((id) => {
    if (heardRef.current.has(id)) return;
    heardRef.current.add(id);
    fetch(`/api/posts/${id}`).catch(() => {});
  }, []);

  const activeMedia = useCallback((track) => {
    const video = isVideoTrack(track);
    const media = video ? videoRef.current : audioRef.current;
    const other = video ? audioRef.current : videoRef.current;
    if (other && !other.paused) other.pause();
    return media;
  }, []);

  const start = useCallback(async (track, { autoplay = true } = {}) => {
    const url = playbackUrl(track);
    const media = activeMedia(track);
    if (!media || !url) throw new Error("No playable audio is available.");
    setCurrent(track);
    setPosition(0);
    setDuration(track.duration || 0);
    setBuffering(true);
    const video = isVideoTrack(track);
    stateRef.current.video = video;
    setVideoMode(video);
    if (media.src !== url) media.src = url;
    media.playbackRate = stateRef.current.speed;
    remember(track);
    noteListen(track._id);
    if (!autoplay) {
      media.pause();
      setPlaying(false);
      setBuffering(false);
      return;
    }
    await media.play();
  }, [activeMedia, noteListen, remember]);

  const playQueue = useCallback(async (list, index = 0, options) => {
    const clean = list.filter((track) => playbackUrl(track));
    if (!clean.length) return;
    const nextIndex = Math.max(0, Math.min(index, clean.length - 1));
    stateRef.current.queue = clean;
    stateRef.current.index = nextIndex;
    setQueue(clean);
    setQueueIndex(nextIndex);
    await start(clean[nextIndex], options);
  }, [start]);

  const playAt = useCallback(async (index) => {
    const list = stateRef.current.queue;
    if (!list[index]) return;
    stateRef.current.index = index;
    setQueueIndex(index);
    await start(list[index]);
  }, [start]);

  const playNext = useCallback(async () => {
    const snapshot = stateRef.current;
    const index = snapshot.shuffle
      ? pickNext(snapshot.queue.length, snapshot.index)
      : snapshot.index + 1;
    await playAt(index);
  }, [playAt]);

  const playPrevious = useCallback(async () => {
    const media = stateRef.current.video ? videoRef.current : audioRef.current;
    if (media && media.currentTime > 3) {
      media.currentTime = 0;
      return;
    }
    await playAt(stateRef.current.index - 1);
  }, [playAt]);

  useEffect(() => {
    const nodes = [audioRef.current, videoRef.current].filter(Boolean);
    const isActive = (media) => media === (stateRef.current.video ? videoRef.current : audioRef.current);
    const onTime = (event) => {
      const media = event.currentTarget;
      if (!isActive(media)) return;
      setPosition(media.currentTime || 0);
      if (media.duration) setDuration(media.duration);
    };
    const onPlay = (event) => {
      if (!isActive(event.currentTarget)) return;
      setPlaying(true);
      setBuffering(false);
    };
    const onPause = (event) => {
      if (!isActive(event.currentTarget)) return;
      setPlaying(false);
    };
    const onWait = (event) => {
      if (!isActive(event.currentTarget)) return;
      setBuffering(true);
    };
    const onCanPlay = (event) => {
      if (!isActive(event.currentTarget)) return;
      setBuffering(false);
    };
    const onEnded = (event) => {
      const media = event.currentTarget;
      if (!isActive(media)) return;
      if (stateRef.current.repeat) {
        media.currentTime = 0;
        media.play().catch(() => {});
        return;
      }
      const snapshot = stateRef.current;
      const index = snapshot.shuffle
        ? pickNext(snapshot.queue.length, snapshot.index)
        : snapshot.index + 1;
      if (snapshot.queue[index]) {
        stateRef.current.index = index;
        setQueueIndex(index);
        start(snapshot.queue[index]).catch(() => {});
      } else {
        setPlaying(false);
      }
    };
    nodes.forEach((media) => {
      media.addEventListener("timeupdate", onTime);
      media.addEventListener("durationchange", onTime);
      media.addEventListener("play", onPlay);
      media.addEventListener("pause", onPause);
      media.addEventListener("waiting", onWait);
      media.addEventListener("canplay", onCanPlay);
      media.addEventListener("ended", onEnded);
    });
    return () => {
      nodes.forEach((media) => {
        media.removeEventListener("timeupdate", onTime);
        media.removeEventListener("durationchange", onTime);
        media.removeEventListener("play", onPlay);
        media.removeEventListener("pause", onPause);
        media.removeEventListener("waiting", onWait);
        media.removeEventListener("canplay", onCanPlay);
        media.removeEventListener("ended", onEnded);
      });
    };
  }, [start]);

  const toggle = useCallback(async () => {
    const media = activeMedia(current);
    if (!media || !current) return;
    if (media.paused) {
      if (media.ended) media.currentTime = 0;
      await media.play();
    } else {
      media.pause();
    }
  }, [activeMedia, current]);

  const stop = useCallback(() => {
    [audioRef.current, videoRef.current].forEach((media) => {
      if (!media) return;
      media.pause();
      media.removeAttribute("src");
      media.load();
    });
    stateRef.current.queue = [];
    stateRef.current.index = 0;
    setCurrent(null);
    setQueue([]);
    setQueueIndex(0);
    setPlaying(false);
    setBuffering(false);
    setPosition(0);
    setDuration(0);
    setVideoMode(false);
  }, []);

  const seek = useCallback((seconds) => {
    const media = isVideoTrack(current) ? videoRef.current : audioRef.current;
    if (!media) return;
    const next = Math.max(0, Math.min(seconds, media.duration || seconds));
    media.currentTime = next;
    setPosition(next);
  }, [current]);

  const cycleSpeed = useCallback(() => {
    const currentSpeed = stateRef.current.speed;
    const next = SPEEDS[(SPEEDS.indexOf(currentSpeed) + 1) % SPEEDS.length];
    stateRef.current.speed = next;
    setSpeed(next);
    [audioRef.current, videoRef.current].forEach((media) => {
      if (media) media.playbackRate = next;
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    stateRef.current.shuffle = !stateRef.current.shuffle;
    setShuffle(stateRef.current.shuffle);
  }, []);

  const toggleRepeat = useCallback(() => {
    stateRef.current.repeat = !stateRef.current.repeat;
    setRepeatOne(stateRef.current.repeat);
  }, []);

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [id, ...prev];
      writeStore(accountId, "favorites", next);
      return next;
    });
  }, [accountId]);

  const rate = useCallback((id, value) => {
    if (!user?.user?._id) return { ok: false, message: "Log in to rate this episode." };
    if (!Number.isInteger(value) || value < 1 || value > 5) return { ok: false, message: "Choose 1 to 5 stars." };
    if (ratings[id]) return { ok: false, message: "You already rated this episode." };
    const next = { ...ratings, [id]: value };
    writeStore(accountId, "ratings", next);
    setRatings(next);
    return { ok: true };
  }, [accountId, ratings, user]);

  const withRatings = useMemo(
    () => tracks.map((track) => ({ ...track, avgRating: ratings[track._id] || 0 })),
    [ratings, tracks]
  );
  const albumsWithCounts = useMemo(
    () => albumSummaries(withRatings, albums),
    [albums, withRatings]
  );
  const recent = useMemo(
    () => recentIds.map((id) => withRatings.find((track) => track._id === id)).filter((track) => track && !isTestEpisode(track)),
    [recentIds, withRatings]
  );
  const saved = useMemo(
    () => favorites.map((id) => withRatings.find((track) => track._id === id)).filter(Boolean),
    [favorites, withRatings]
  );

  const value = {
    tracks: withRatings,
    testTracks,
    albums: albumsWithCounts,
    loading,
    error,
    ready,
    refresh,
    favorites,
    toggleFavorite,
    ratings,
    rate,
    recent,
    saved,
    current,
    queue,
    queueIndex,
    playing,
    buffering,
    position,
    duration,
    speed,
    shuffle,
    repeatOne,
    videoMode,
    videoRef,
    playQueue,
    playAt,
    playNext,
    playPrevious,
    toggle,
    stop,
    seek,
    cycleSpeed,
    toggleShuffle,
    toggleRepeat,
    loggedIn: !!user?.user?._id,
  };

  return (
    <PodcastContext.Provider value={value}>
      <audio ref={audioRef} className="pod-media" preload="metadata" />
      <video ref={videoRef} className={videoMode ? "pod-media is-video" : "pod-media"} playsInline preload="metadata" />
      {children}
    </PodcastContext.Provider>
  );
}

export function usePodcast() {
  const value = useContext(PodcastContext);
  if (!value) throw new Error("Podcast provider is missing");
  return value;
}
